import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import shieldAlt from '@iconify/icons-fa-solid/shield-alt';
import boltIcon from '@iconify/icons-fa-solid/bolt';
import cartPlus from '@iconify/icons-fa-solid/cart-plus';
import handHoldingUsd from '@iconify/icons-fa-solid/hand-holding-usd';
import { storage } from '@/utils';
import CTAButton from '@/components/cta/button';
import styles from './MobileP2PPanel.module.css';

const TRADE_PATH = '/trade';
const LOGIN_PATH = '/login?modalType=auth-signin';

/* ─── Compact P2P Symbol ─── */
const P2PSymbolCompact = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 40 40"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <defs>
      <linearGradient id="mp2pGradA" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#F5D76E" />
        <stop offset="100%" stopColor="#B8962E" />
      </linearGradient>
      <linearGradient id="mp2pGradB" x1="1" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#26A17B" />
        <stop offset="100%" stopColor="#16A34A" />
      </linearGradient>
    </defs>

    {/* Left peer */}
    <circle cx="7" cy="20" r="5" stroke="url(#mp2pGradA)" strokeWidth="1.4" fill="rgba(245, 215, 110, 0.08)" />
    <circle cx="7" cy="18" r="1.6" fill="url(#mp2pGradA)" />
    <path d="M3.5 23.5 C 4.2 21.8, 9.8 21.8, 10.5 23.5" stroke="url(#mp2pGradA)" strokeWidth="1.4" strokeLinecap="round" fill="none" />

    {/* Right peer */}
    <circle cx="33" cy="20" r="5" stroke="url(#mp2pGradB)" strokeWidth="1.4" fill="rgba(38, 161, 123, 0.08)" />
    <circle cx="33" cy="18" r="1.6" fill="url(#mp2pGradB)" />
    <path d="M29.5 23.5 C 30.2 21.8, 35.8 21.8, 36.5 23.5" stroke="url(#mp2pGradB)" strokeWidth="1.4" strokeLinecap="round" fill="none" />

    {/* Exchange arrows */}
    <path d="M14 16.5 L 25 16.5" stroke="url(#mp2pGradA)" strokeWidth="1.2" strokeLinecap="round" />
    <path d="M23.2 14.5 L 25.4 16.5 L 23.2 18.5" stroke="url(#mp2pGradA)" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
    <path d="M26 23.5 L 15 23.5" stroke="url(#mp2pGradB)" strokeWidth="1.2" strokeLinecap="round" />
    <path d="M16.8 21.5 L 14.6 23.5 L 16.8 25.5" stroke="url(#mp2pGradB)" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" fill="none" />

    {/* Center coin */}
    <circle cx="20" cy="20" r="4.2" fill="#0f1520" stroke="url(#mp2pGradA)" strokeWidth="1.1" />
    <text x="20" y="22" textAnchor="middle" fontSize="3.8" fontWeight="900" fill="#F5D76E" fontFamily="system-ui, sans-serif">₮</text>
  </svg>
);

/* ─── Golden Diamond Badge ─── */
const GoldenBadge = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 40 40"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <defs>
      <linearGradient id="mGoldBadgeGrad" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#FFE98A" />
        <stop offset="45%" stopColor="#F5D76E" />
        <stop offset="100%" stopColor="#B8962E" />
      </linearGradient>
      <linearGradient id="mGoldBadgeShine" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#FFF5C8" stopOpacity="0.9" />
        <stop offset="100%" stopColor="#FFF5C8" stopOpacity="0" />
      </linearGradient>
      <radialGradient id="mGoldBadgeGlow" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="#F5D76E" stopOpacity="0.5" />
        <stop offset="100%" stopColor="#F5D76E" stopOpacity="0" />
      </radialGradient>
    </defs>

    {/* Outer glow */}
    <circle cx="20" cy="20" r="18" fill="url(#mGoldBadgeGlow)" />

    {/* Diamond shape */}
    <path
      d="M20 5 L32 17 L20 35 L8 17 Z"
      fill="url(#mGoldBadgeGrad)"
      stroke="#B8962E"
      strokeWidth="0.8"
      strokeLinejoin="round"
    />

    {/* Top facet */}
    <path d="M20 5 L14 17 L20 17 L26 17 Z" fill="url(#mGoldBadgeGrad)" opacity="0.85" />
    <path d="M8 17 L14 17 M20 17 L20 35 M26 17 L32 17" stroke="#8B6E1E" strokeWidth="0.6" opacity="0.6" />
    <path d="M20 5 L14 17 M20 5 L26 17" stroke="#8B6E1E" strokeWidth="0.6" opacity="0.5" />

    {/* Shine */}
    <path d="M20 6 L25 16 L22 16 Z" fill="url(#mGoldBadgeShine)" />

    {/* Center star */}
    <path
      d="M20 13 L21 17 L25 17 L22 19.5 L23 23.5 L20 21 L17 23.5 L18 19.5 L15 17 L19 17 Z"
      fill="#FFF5C8"
      opacity="0.95"
    />
  </svg>
);

const MobileP2PPanel = () => {
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  useEffect(() => {
    const logged =
      storage.getKey('logged') === true ||
      localStorage.getItem('logged') === 'true';
    setIsLoggedIn(!!logged);
  }, []);

  const goToTrade = useCallback(
    () => navigate(isLoggedIn ? TRADE_PATH : LOGIN_PATH),
    [navigate, isLoggedIn],
  );

  return (
    <div className={styles.panel} onClick={goToTrade} role="button" tabIndex={0}>
      <div className={styles.bgGlow} />
      <div className={styles.borderGlow} />

      {/* Floating golden premium badge */}
      <div className={styles.goldBadgeFloat}>
        <GoldenBadge className={styles.goldBadgeSvg} />
        <span className={styles.goldBadgeLabel}>PREMIUM</span>
      </div>

      <div className={styles.content}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.iconWrap}>
            <div className={styles.iconRingPulse} />
            <img style={{objectFit:'contain'}} src="/assets/p2p-icon.png" alt="P2P" className={styles.p2pSvg} />
          </div>

          <div className={styles.headText}>
            <span className={styles.title}>P2P TRADE</span>
            <span className={styles.subtitle}>PEER · TO · PEER</span>
          </div>

          <div className={styles.liveTag}>
            <span className={styles.liveDot} />
            <span className={styles.liveText}>LIVE</span>
          </div>
        </div>

        {/* Highlighted Stats Strip */}
        <div className={styles.highlightStrip}>
          <div className={styles.highlightItem}>
            <span className={styles.highlightValue}>0%</span>
            <span className={styles.highlightLabel}>Fee</span>
          </div>
          <span className={styles.highlightDivider} />
          <div className={styles.highlightItem}>
            <span className={styles.highlightValueGreen}>~30s</span>
            <span className={styles.highlightLabel}>Release</span>
          </div>
          <span className={styles.highlightDivider} />
          <div className={styles.highlightItem}>
            <span className={styles.highlightValueGold}>24/7</span>
            <span className={styles.highlightLabel}>Support</span>
          </div>
        </div>

        {/* Trust strip */}
        <div className={styles.trustStrip}>
          <span className={styles.trustItem}>
            <Icon icon={shieldAlt} width={9} height={9} className={styles.trustGreen} />
            Escrow
          </span>
          <span className={styles.trustDot} />
          <span className={styles.trustItem}>
            <Icon icon={boltIcon} width={9} height={9} className={styles.trustGold} />
            Instant
          </span>
          <span className={styles.trustDot} />
          <span className={styles.trustItem}>
            <span className={styles.trustDotGreen} />
            Verified
          </span>
        </div>

        {/* Buy / Sell buttons */}
        <div className={styles.actions}>
          <button
            className={`${styles.chip} ${styles.chipBuy}`}
            onClick={(e) => { e.stopPropagation(); goToTrade(); }}
            type="button"
          >
            <span className={styles.chipShimmer} />
            <Icon icon={cartPlus} width={12} height={12} className={styles.chipIcon} />
            <span className={styles.chipLabel}>Buy USDT</span>
          </button>
          <button
            className={`${styles.chip} ${styles.chipSell}`}
            onClick={(e) => { e.stopPropagation(); goToTrade(); }}
            type="button"
          >
            <span className={styles.chipShimmer} />
            <Icon icon={handHoldingUsd} width={12} height={12} className={styles.chipIcon} />
            <span className={styles.chipLabel}>Sell USDT</span>
          </button>
        </div>

        {/* Full-width CTA */}
        <div className={styles.ctaWrap}>
          <CTAButton onClick={goToTrade}>
            {isLoggedIn ? 'Go to Trade →' : 'Start Trading →'}
          </CTAButton>
        </div>
      </div>
    </div>
  );
};

export default MobileP2PPanel;